import { Button, Colors, Intent, NonIdealState } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { useSet } from "ahooks";
import {
  ButtonsBloc,
  DataTable,
  FGCustomPanel,
  FGMaskInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
  InlineButtonContainer,
  useGridState,
  useSearchApi
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import styled, { css } from "styled-components";

import { AddButton, SelectButton, SmallFormGenerator } from "../..";
import {
  ApprenantSelectorApi,
  ApprenantSelectorGridDto,
  ApprenantSelectorSearch,
  ApprenantSelectorSearchFromJSON
} from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { useApiService, useTabMessage, useTheme, useTl } from "../../../hooks";
import { useReferential } from "../../../hooks/useReferential";
import { ETLCodes } from "../../../locales";
import { Guid } from "../../../utils/guid";
import { FGWalterDateMaskInput } from "../FGWalterDateMaskInput";
import { FGWalterSelectInput } from "../FGWalterSelectInput";

const Container = styled.div`
  margin: 1rem 1rem 0 1rem;
`;

export interface IApprenantSelectorProps {
  excludedApprenantIds?: number[];
  onApprenantSelected: (selectedApprenantIds?: number[]) => void;
  rightElement?: any;
  allowMultipleSelection?: boolean;
  initialSearch?: boolean;
  preSelectedIds?: number[];
}

export const ApprenantSelector: React.FunctionComponent<IApprenantSelectorProps> = ({
  excludedApprenantIds = [],
  onApprenantSelected,
  rightElement,
  allowMultipleSelection = false,
  initialSearch = true,
  preSelectedIds = []
}) => {
  const { t } = useTl();
  const history = useHistory();
  const { theme } = useTheme();
  const lastSearchObject = React.useRef<ApprenantSelectorSearch>();
  const [nationalites, nLoading] = useReferential(a => a.referentialGetAllNationalite(), false, []);
  const [selectedApprenantIds, { add: addApprenantId, remove: removeApprenantId }] = useSet<number>(preSelectedIds);
  const api = useApiService(ApprenantSelectorApi);
  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [10, 15],
    pageSize: 10,
    sortKeys: { nom: "ASC", prenom: "ASC" }
  });

  const initialData = React.useMemo(() => {
    return ApprenantSelectorSearchFromJSON({
      selectedApprenantIds,
      excludedApprenantIds,
      forceSkip: 0,
      forceTake: 10
    });
  }, [excludedApprenantIds, selectedApprenantIds]);

  const { totalCount } = tableState;
  const searchFunction = React.useCallback(
    (sObj?: ApprenantSelectorSearch) => {
      let nextSObj = { ...sObj };
      Object.keys(sObj)
        .filter(k => !["forceSkip", "forceTake", "filter", "sortKey", "aggregateKeys", "parameters"].includes(k))
        .forEach(k => {
          nextSObj[k] = !!sObj[k]?.value ? sObj[k] : undefined;
        });
      lastSearchObject.current = sObj;
      return api.apprenantSelectorBaseSearch({
        ApprenantSelectorSearch: {
          ...nextSObj,
          selectedApprenantIds: Array.from(selectedApprenantIds),
          excludedApprenantIds
        }
      });
    },
    [api, excludedApprenantIds, selectedApprenantIds]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: initialSearch
  });

  const selectApprenant = React.useCallback(
    (id: number) => {
      if (allowMultipleSelection) {
        selectedApprenantIds.has(id) ? removeApprenantId(id) : addApprenantId(id);
      } else {
        onApprenantSelected([id]);
      }
    },
    [addApprenantId, allowMultipleSelection, onApprenantSelected, removeApprenantId, selectedApprenantIds]
  );

  const onRowClick = React.useCallback(
    (item: ApprenantSelectorGridDto) => {
      selectApprenant(item.idapprenant);
    },
    [selectApprenant]
  );

  const identifier = React.useMemo(() => Guid.newGuid(), []);
  const { messageValue, clearMessageValue, openTabThenSendMessage } = useTabMessage(identifier);
  const add = React.useCallback(
    values => {
      openTabThenSendMessage(`#${ERoutes.apprenant}/0/signaletique/checkDoublons`, values);
    },
    [openTabThenSendMessage]
  );

  React.useEffect(() => {
    if (messageValue) {
      search();
      selectApprenant(messageValue);
      clearMessageValue();
    }
  }, [clearMessageValue, messageValue, search, selectApprenant]);

  const columns = React.useMemo(
    () => [
      {
        header: () => t(ETLCodes.Nom),
        fieldName: "nom"
      },
      {
        header: () => t(ETLCodes.Prenom),
        fieldName: "prenom"
      },
      {
        header: () => t(ETLCodes.DateNaissance),
        fieldName: "dateNaissance"
      },
      {
        header: () => t(ETLCodes.NumeroNational),
        fieldName: "registreNational"
      },
      {
        header: () => t(ETLCodes.Adresse),
        fieldName: "adresse"
      },
      {
        header: () => t(ETLCodes.CodePostal),
        fieldName: "codePostal"
      },
      {
        header: () => t(ETLCodes.Localite),
        fieldName: "localite"
      },
      {
        header: () => t(ETLCodes.Nationalite),
        fieldName: "nationalite"
      }
    ],
    [t]
  );

  const customizeRowStyleCallback = React.useCallback(
    (item: ApprenantSelectorGridDto) => {
      const isSelected = selectedApprenantIds.has(item.idapprenant);

      return css`
        & * {
          color: ${isSelected ? Colors.WHITE : Colors.BLACK} !important;
        }

        & & > td {
          background-color: ${isSelected ? theme.primaryColor : "initial"} !important;
        }

        background-color: ${isSelected ? theme.primaryColor : "transparent"} !important;
      `;
    },
    [selectedApprenantIds, theme.primaryColor]
  );

  const onApprenantSelect = React.useCallback(() => {
    onApprenantSelected(Array.from(selectedApprenantIds));
  }, [onApprenantSelected, selectedApprenantIds]);

  const finalRightElements = React.useMemo(() => {
    return allowMultipleSelection ? (
      <>
        <SelectButton onClick={onApprenantSelect}></SelectButton>
        {rightElement}
      </>
    ) : (
      rightElement
    );
  }, [allowMultipleSelection, onApprenantSelect, rightElement]);

  const tableTitle = React.useMemo(
    () =>
      `${t(ETLCodes.TableResults, { count: totalCount })}${
        selectedApprenantIds.size > 0 ? " - " + selectedApprenantIds.size + " " + t(ETLCodes.SelectedApprenants) : ""
      }`,
    [selectedApprenantIds.size, t, totalCount]
  );

  return (
    <Container>
      <SmallFormGenerator
        enableDirtyCheck={false}
        initialValues={initialData}
        onSubmit={c => search(c)}
        editMode
        hideButtons={true}
        onCancel={() => history.push(`${ERoutes.apprenant}`)}
      >
        <FieldGroup columns={2}>
          <FGTextInput name="nom.value" label={t(ETLCodes.Nom)} maxLength={50} forceCase="upper" />
          <FGWalterSelectInput
            name="nationalite.value"
            label={t(ETLCodes.Nationalite)}
            items={nationalites}
            loading={nLoading}
          />
          <FGTextInput name="prenom.value" label={t(ETLCodes.Prenom)} maxLength={50} />
          <FGMaskInput
            name="regitreNational.value"
            label={t(ETLCodes.NumeroNational)}
            cleaveOptions={{ delimiters: [".", ".", "-", "."], blocks: [2, 2, 2, 3, 2], numericOnly: true }}
          />
          <FGWalterDateMaskInput name="dateNaissance.value" label={t(ETLCodes.DateNaissance)} />
        </FieldGroup>
        <FieldGroup>
          <FGCustomPanel>
            {ctx => (
              <InlineButtonContainer>
                <ButtonsBloc></ButtonsBloc>
                <ButtonsBloc>
                  <Button icon={IconNames.SEARCH} type="submit" text={t(ETLCodes.Search)} intent={Intent.PRIMARY} />
                  <AddButton
                    onClick={() => add(ctx.formik?.values)}
                    text={t(ETLCodes.General_Add)}
                    intent={Intent.PRIMARY}
                    minimal={false}
                  />
                </ButtonsBloc>
              </InlineButtonContainer>
            )}
          </FGCustomPanel>
        </FieldGroup>
        <FGCustomPanel>
          {ctx => (
            <FieldSet title={tableTitle} rightElement={finalRightElements}>
              <DataTable
                dateFormat="dd-MM-yyyy"
                tableState={tableState}
                loading={loading}
                columns={columns}
                onRowClick={onRowClick}
                customizeRowStyle={customizeRowStyleCallback}
                htmlTableOptions={{ striped: true, interactive: true, bordered: true }}
                renderNoData={
                  <NonIdealState
                    icon="search"
                    title={t(
                      !!lastSearchObject.current ? ETLCodes.GeneralNoData : ETLCodes.VeuillezEffectuerUneRecherche
                    )}
                  />
                }
              />
            </FieldSet>
          )}
        </FGCustomPanel>
      </SmallFormGenerator>
    </Container>
  );
};
